import type { GulaOrder } from "./gulaTypes";
import { shortDay } from "./deliveryDates";

/**
 * Whether the client really has the unit, for the Operaciones cards. A unit is
 * only "Entregado" when someone confirmed it: Packlink (the carrier's tracking)
 * or the client. Everything else says plainly that it has not arrived yet.
 */

export type DeliveryTone = "ok" | "warn" | "bad" | "muted";

export interface DeliveryView {
  tone: DeliveryTone;
  title: string;
  /** Who confirmed it, or what is expected next */
  detail: string;
  /** Last carrier event: "Mercancia en reparto · 26 oct, 07:54 · Marbella" */
  event: string;
  /** "Comprobado hace 5 min" for Packlink shipments not delivered yet */
  checked: string;
  /** The installer got the parte before the delivery: tell them it arrived */
  installerNeedsUpdate: boolean;
  /** Packlink is being checked automatically */
  tracked: boolean;
}

type DeliveryOrder = Pick<
  GulaOrder,
  | "stage"
  | "received_at"
  | "received_source"
  | "installer_notified_at"
  | "packlink_reference"
  | "packlink_service"
  | "expected_delivery_date"
  | "delivery_status"
  | "delivery_event"
  | "delivery_event_at"
  | "delivery_event_city"
  | "delivery_checked_at"
>;

const TIME_ZONE = "Europe/Madrid";

/** "lun 26 oct, 13:20" in Spain. */
export const dayAndTime = (iso: string): string =>
  new Date(iso).toLocaleString("es-ES", {
    timeZone: TIME_ZONE,
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

/** "hace 5 min", "hace 2 h", "hace 3 d". */
export const ago = (iso: string, now: Date): string => {
  const minutes = Math.max(
    0,
    Math.round((now.getTime() - new Date(iso).getTime()) / 60_000),
  );
  if (minutes < 1) return "ahora mismo";
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  return `hace ${Math.round(hours / 24)} d`;
};

/** First part of "SEUR · Shop2Home". */
export const carrierName = (service?: string | null): string =>
  service?.split(" · ")[0]?.trim() || "el transportista";

const SOURCE_DETAIL: Record<string, string> = {
  whatsapp: "Lo confirmó el cliente por WhatsApp",
  phone: "Lo confirmó el cliente por teléfono",
};

const describeEvent = (order: DeliveryOrder): string =>
  order.delivery_event
    ? [
        order.delivery_event,
        order.delivery_event_at ? dayAndTime(order.delivery_event_at) : null,
        order.delivery_event_city,
      ]
        .filter(Boolean)
        .join(" · ")
    : "";

const expected = (order: DeliveryOrder) =>
  order.expected_delivery_date
    ? `Entrega prevista: ${shortDay(order.expected_delivery_date)}.`
    : "";

export const deliveryView = (
  order: DeliveryOrder,
  now: Date = new Date(),
): DeliveryView => {
  const tracked = !!order.packlink_reference;
  const event = describeEvent(order);
  const checked =
    tracked && order.delivery_checked_at && !order.received_at
      ? `Comprobado en Packlink ${ago(order.delivery_checked_at, now)}`
      : "";
  const base = { event, checked, tracked, installerNeedsUpdate: false };

  if (order.received_at) {
    return {
      ...base,
      tone: "ok",
      title: `Entregado · ${dayAndTime(order.received_at)}`,
      detail:
        order.received_source === "packlink"
          ? `Lo confirma ${carrierName(order.packlink_service)} en Packlink`
          : (SOURCE_DETAIL[order.received_source ?? ""] ??
            "Marcado como recibido"),
      event: order.received_source === "packlink" ? event : "",
      installerNeedsUpdate:
        order.stage === "installer_notified" &&
        !!order.installer_notified_at &&
        new Date(order.installer_notified_at).getTime() <
          new Date(order.received_at).getTime(),
    };
  }

  if (!tracked) {
    return {
      ...base,
      tone: "muted",
      title: "Entrega sin confirmar",
      detail: [
        "Envío sin seguimiento automático: confírmalo con el cliente.",
        expected(order),
      ]
        .filter(Boolean)
        .join(" "),
    };
  }

  const carrier = carrierName(order.packlink_service);
  switch (order.delivery_status) {
    case "delivered":
      return {
        ...base,
        tone: "ok",
        title: "Entregado",
        detail: `Lo confirma ${carrier} en Packlink`,
      };
    case "out_for_delivery":
      return {
        ...base,
        tone: "warn",
        title: "En reparto · aún no entregado",
        detail: `${carrier} lo está repartiendo hoy.`,
      };
    case "in_transit":
      return {
        ...base,
        tone: "warn",
        title: "En camino · aún no entregado",
        detail: expected(order),
      };
    case "not_shipped":
      return {
        ...base,
        tone: "bad",
        title: "Aún no ha salido",
        detail: `${carrier} todavía no lo ha recogido.`,
      };
    case "incident":
      return {
        ...base,
        tone: "bad",
        title: "Incidencia en el envío · no entregado",
        detail: "Revisadla en Packlink y avisad al cliente.",
      };
    case "returned":
      return {
        ...base,
        tone: "bad",
        title: "Devuelto al remitente",
        detail: "El paquete vuelve a la nave.",
      };
    default:
      return {
        ...base,
        tone: "muted",
        title: "Entrega sin comprobar",
        detail: "Pulsa actualizar para preguntar a Packlink.",
      };
  }
};

/** Orders on their way whose delivery is not confirmed yet. */
export const isAwaitingDelivery = (order: DeliveryOrder): boolean =>
  !order.received_at && ["shipped", "installer_notified"].includes(order.stage);
